// Klusje kiezen (B32): from the block Eventueel klusje, a task from the list straight into that block.
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCategories } from "../lib/categories";
import { db, type DayBlock } from "../lib/db";
import { planTask } from "../lib/tasks";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

export function ChorePicker({ date, block }: { date: string; block: DayBlock }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" className="mt-1" onClick={() => setOpen(true)}>
        <Plus aria-hidden className="size-5" />
        Klusje kiezen
      </Button>
      {open && <ChoreSheet date={date} block={block} onClose={() => setOpen(false)} />}
    </>
  );
}

function ChoreSheet({ date, block, onClose }: { date: string; block: DayBlock; onClose: () => void }) {
  const categories = useCategories();
  // Klusjes first: the category of the block's own kind of work, when it is there.
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const tasks = useLiveQuery(async () =>
    (await db.tasks.toArray())
      .filter((t) => !t.deleted_at && (t.status === "open" || t.status === "inbox"))
      .sort((a, b) => a.position - b.position || (a.created_at < b.created_at ? -1 : 1)),
  );
  if (!categories || !tasks) return null;
  const klusjes = categories.find((c) => c.seed_key === "klusjes")?.id ?? null;
  const current = chosen === undefined ? klusjes : chosen;
  const shown = current ? tasks.filter((t) => t.category_id === current) : tasks;

  async function pick(taskId: string, title: string) {
    const ok = await planTask(taskId, { date, dayBlockId: block.id });
    toast(ok ? `${title} in ${block.title}` : "Dat klusje kan niet meer op deze dag.");
    onClose();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title="Klusje kiezen" description={`Komt in ${block.title}`}>
      <div role="radiogroup" aria-label="Categorie" className="mb-3 flex flex-wrap gap-2">
        {[{ id: null as string | null, name: "Alles" }, ...categories].map((c) => (
          <button
            key={c.id ?? "alles"}
            type="button"
            role="radio"
            aria-checked={current === c.id}
            onClick={() => setChosen(c.id)}
            className={cn(
              "min-h-11 rounded-xl border-2 px-3 [overflow-wrap:anywhere]",
              current === c.id ? "border-pine bg-pine text-paper" : "border-mist bg-paper text-ink",
            )}
          >
            {c.name}
          </button>
        ))}
      </div>
      {shown.length ? (
        <ul className="divide-y divide-mist rounded-2xl border border-mist bg-paper">
          {shown.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => void pick(t.id, t.title)}
                className="flex min-h-12 w-full items-center gap-3 px-3 text-left"
              >
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{t.title}</span>
                {t.estimate_minutes && <span className="text-sm text-ink-soft tabular">{t.estimate_minutes} min</span>}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-ink-soft">Hier staat nog niets. Leg een klusje vast met de plusknop, of kies Alles.</p>
      )}
    </Sheet>
  );
}
